import * as Y from 'yjs';
import type { BoardData, CollectionName, EntityOf, Item, Meta } from '../model/schema.js';
import { assignUniqueLabel } from '../model/labels.js';
import { BoardData as BoardDataSchema, COLLECTIONS, COLLECTION_SCHEMAS } from '../model/schema.js';

/**
 * Typed access to a board Y.Doc. Every collection is a Y.Map<id, plain JSON entity>.
 * Entities are replaced as a whole (entity-level last-writer-wins), which is safe with
 * a single editor at a time (decision R4) and keeps the model trivially serialisable.
 */

export function collection<C extends CollectionName>(doc: Y.Doc, name: C): Y.Map<EntityOf<C>> {
  return doc.getMap<EntityOf<C>>(name);
}

export function metaMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>('meta');
}

export function getMeta(doc: Y.Doc): Meta {
  const m = metaMap(doc);
  const meta: Meta = {
    schemaVersion: (m.get('schemaVersion') as number | undefined) ?? 1,
    name: (m.get('name') as string | undefined) ?? '',
    units: (m.get('units') as Meta['units'] | undefined) ?? 'metric',
  };
  const defs = m.get('propertyDefs') as Meta['propertyDefs'];
  if (defs !== undefined) meta.propertyDefs = defs;
  const materials = m.get('materialList') as Meta['materialList'];
  if (materials !== undefined) meta.materialList = materials;
  const symbols = m.get('symbolStyle') as Meta['symbolStyle'];
  if (symbols !== undefined) meta.symbolStyle = symbols;
  const hideEmpty = m.get('hideEmptyLayers') as Meta['hideEmptyLayers'];
  if (hideEmpty !== undefined) meta.hideEmptyLayers = hideEmpty;
  const polar = m.get('polarHints') as Meta['polarHints'];
  if (polar !== undefined) meta.polarHints = polar;
  const mixes = m.get('mixBadges') as Meta['mixBadges'];
  if (mixes !== undefined) meta.mixBadges = mixes;
  const outputs = m.get('outputBadges') as Meta['outputBadges'];
  if (outputs !== undefined) meta.outputBadges = outputs;
  const prefix = m.get('busPrefix') as Meta['busPrefix'];
  if (prefix !== undefined) meta.busPrefix = prefix;
  const fields = m.get('labelFields') as Meta['labelFields'];
  if (fields !== undefined) meta.labelFields = fields;
  const tv = m.get('rfTvChannels') as Meta['rfTvChannels'];
  if (tv !== undefined) meta.rfTvChannels = tv;
  const rider = m.get('rider') as Meta['rider'];
  if (rider !== undefined) meta.rider = rider;
  return meta;
}

export function setMeta(doc: Y.Doc, patch: Partial<Meta>, origin?: unknown): void {
  doc.transact(() => {
    const m = metaMap(doc);
    for (const [k, v] of Object.entries(patch)) m.set(k, v);
  }, origin);
}

export function getAll<C extends CollectionName>(doc: Y.Doc, name: C): EntityOf<C>[] {
  return Array.from(collection(doc, name).values());
}

export function getOne<C extends CollectionName>(
  doc: Y.Doc,
  name: C,
  id: string,
): EntityOf<C> | undefined {
  return collection(doc, name).get(id);
}

/** Validates and stores an entity. Throws on schema violations (programming error). */
export function upsert<C extends CollectionName>(
  doc: Y.Doc,
  name: C,
  entity: EntityOf<C>,
  origin?: unknown,
): void {
  const parsed = COLLECTION_SCHEMAS[name].parse(entity) as EntityOf<C>;
  doc.transact(() => {
    if (name === 'items') uniqueItemLabel(doc, parsed as Item);
    collection(doc, name).set(parsed.id, parsed);
  }, origin);
}

/**
 * Item labels are unique (OIL-68): a new or relabelled item whose label is taken gets the next
 * number of its group, and a plain namesake becomes "(1)" – in the same transaction, so one undo
 * step. Items written with their unchanged label are left alone.
 */
function uniqueItemLabel(doc: Y.Doc, item: Item): void {
  const items = collection(doc, 'items');
  if (items.get(item.id)?.label === item.label) return;
  const others = [...items.values()].filter((o) => o.id !== item.id);
  const a = assignUniqueLabel(item.label, others);
  item.label = a.label;
  for (const r of a.renames) {
    items.set(r.id, { ...items.get(r.id)!, label: r.label });
  }
}

/** Shallow-merges a patch into an existing entity. Returns false when the entity is missing. */
export function patch<C extends CollectionName>(
  doc: Y.Doc,
  name: C,
  id: string,
  changes: Partial<EntityOf<C>>,
  origin?: unknown,
): boolean {
  const current = getOne(doc, name, id);
  if (current === undefined) return false;
  upsert(doc, name, { ...current, ...changes, id } as EntityOf<C>, origin);
  return true;
}

export function remove(
  doc: Y.Doc,
  name: CollectionName,
  ids: readonly string[],
  origin?: unknown,
): void {
  doc.transact(() => {
    const map = collection(doc, name);
    for (const id of ids) map.delete(id);
  }, origin);
}

/** Replaces the complete content of the doc with `data` (init, snapshot restore, import). */
export function loadBoardData(doc: Y.Doc, data: BoardData, origin?: unknown): void {
  const valid = BoardDataSchema.parse(data);
  doc.transact(() => {
    const m = metaMap(doc);
    m.clear();
    for (const [k, v] of Object.entries(valid.meta)) m.set(k, v);
    for (const name of COLLECTIONS) {
      const map = collection(doc, name);
      map.clear();
      for (const [id, entity] of Object.entries(valid[name])) map.set(id, entity as never);
    }
  }, origin);
}

export function toBoardData(doc: Y.Doc): BoardData {
  const out = { meta: getMeta(doc) } as BoardData;
  for (const name of COLLECTIONS) {
    (out as Record<string, unknown>)[name] = Object.fromEntries(collection(doc, name).entries());
  }
  return out;
}

export function docFromBoardData(data: BoardData): Y.Doc {
  const doc = new Y.Doc();
  loadBoardData(doc, data);
  return doc;
}

/** Undo manager over all collections, tracking only the given origin (per-user undo, decision T6). */
export function createUndoManager(doc: Y.Doc, origin: unknown): Y.UndoManager {
  const scopes = [metaMap(doc), ...COLLECTIONS.map((n) => collection(doc, n))];
  return new Y.UndoManager(scopes, { trackedOrigins: new Set([origin]), captureTimeout: 400 });
}
